import { Component, Prop, h, Element, State } from '@stencil/core';
import Swiper from 'swiper';
import { Navigation, Autoplay } from 'swiper/modules';

@Component({
  tag: 'qumra-slider-multi',
  styleUrl: 'qumra-slider-multi.css',
  shadow: false,
})
export class QumraSliderMulti {
  @Element() el: HTMLElement;
  @Prop() slides: { content: string }[] = [];
  @Prop() slidesPerView: string = '3'; // desktop
  @Prop() slidesPerViewSm: string = '2'; // mobile default
  @Prop() spaceBetween: string = '30';
  @Prop() navigationColor: string = "#000";
  @Prop() navigation: boolean = true;
  @Prop() centered: boolean = false;

  @State() uniqueId: string = `qumra-slider-multi-${Math.random().toString(36).substr(2, 9)}`;

  componentDidLoad() {
    const parsedSlidesPerViewSm = parseInt(this.slidesPerViewSm);
    const parsedSlidesPerView = parseInt(this.slidesPerView);
    const parsedSpaceBetween = parseInt(this.spaceBetween);

    const slidesPerViewSm = isNaN(parsedSlidesPerViewSm) ? 2 : parsedSlidesPerViewSm;
    const slidesPerView = isNaN(parsedSlidesPerView) ? 3 : parsedSlidesPerView;
    const spaceBetween = isNaN(parsedSpaceBetween) ? 30 : parsedSpaceBetween;

    const swiper = new Swiper(`.${this.uniqueId} .mySwiper-multi`, {
      slidesPerView: slidesPerViewSm,
      spaceBetween,
      modules: [Navigation, Autoplay],
      centeredSlides: this.centered,
      loop: true,
      autoplay: {
        delay: 3000,
        disableOnInteraction: false,
      },
      navigation: {
        nextEl: `.${this.uniqueId} .swiper-button-next`,
        prevEl: `.${this.uniqueId} .swiper-button-prev`,
      },
      breakpoints: {
        0: {
          slidesPerView: slidesPerViewSm,
          spaceBetween: 10,
        },
        640: {
          slidesPerView: slidesPerViewSm,
          spaceBetween: 20,
        },
        768: {
          slidesPerView: 3,
          spaceBetween: 20,
        },
        1024: {
          slidesPerView,
          spaceBetween: 20,
        },
      },
    });

    swiper.on('changeDirection', () => {
    });

    swiper.on('init', () => {
    });
  }

  hexToRgb(hex: string) {
    hex = hex.replace('#', '');
    if (hex.length === 3) {
      hex = hex.split('').map((x) => x + x).join('');
    }
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return {
      r: isNaN(r) ? 0 : r,
      g: isNaN(g) ? 0 : g,
      b: isNaN(b) ? 0 : b,
    };
  }

  lightenColor(rgb: { r: number; g: number; b: number }, percentage: number) {
    return {
      r: Math.round(rgb.r + (255 - rgb.r) * percentage),
      g: Math.round(rgb.g + (255 - rgb.g) * percentage),
      b: Math.round(rgb.b + (255 - rgb.b) * percentage),
    };
  }

  render() {
    const rgbColor = this.hexToRgb(this.navigationColor);
    const lightenedColor = this.lightenColor(rgbColor, 0.9);
    const lightenedBadgeColor = `rgb(${lightenedColor.r}, ${lightenedColor.g}, ${lightenedColor.b})`;

    return (
      <div class={`swiper-container ${this.uniqueId}`}>
        <div class="swiper py-3 mySwiper-multi">
          <div class="swiper-wrapper">
            <slot></slot>
          </div>
        </div>

        {this.navigation && (
          <div>
            <button
              aria-label="UInavigation-swiper-button-next-multi"
              class={`${this.uniqueId} swiper-button-next`}
              style={{ backgroundColor: lightenedBadgeColor }}
            >
              <span class="text-mainColor" style={{ color: this.navigationColor }}>
                <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M8.65115 18.2238C8.30664 17.8655 8.31781 17.2958 8.67611 16.9512L13.4014 12.5L8.67611 8.04875C8.31781 7.70424 8.30664 7.1345 8.65115 6.7762C8.99567 6.41791 9.56541 6.40674 9.9237 6.75125L15.3237 11.8512C15.5002 12.0209 15.5999 12.2552 15.5999 12.5C15.5999 12.7448 15.5002 12.9791 15.3237 13.1487L9.9237 18.2487C9.56541 18.5933 8.99567 18.5821 8.65115 18.2238Z"
                    fill={this.navigationColor}
                    stroke={this.navigationColor}
                    stroke-width="0.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </span>
            </button>
            <button
              aria-label="UInavigation-swiper-button-prev-multi"
              class={`${this.uniqueId} swiper-button-prev`}
              style={{ backgroundColor: lightenedBadgeColor }}
            >
              <span class="text-mainColor" style={{ color: this.navigationColor }}>
                <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M15.3488 6.7762C15.6934 7.1345 15.6822 7.70424 15.3239 8.04875L10.5986 12.5L15.3239 16.9513C15.6822 17.2958 15.6934 17.8655 15.3488 18.2238C15.0043 18.5821 14.4346 18.5933 14.0763 18.2487L8.6763 13.1488C8.49983 12.9791 8.4001 12.7448 8.4001 12.5C8.4001 12.2552 8.49983 12.0209 8.6763 11.8513L14.0763 6.75125C14.4346 6.40674 15.0043 6.41791 15.3488 6.7762Z"
                    fill={this.navigationColor}
                    stroke={this.navigationColor}
                    stroke-width="0.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </span>
            </button>
          </div>
        )}
      </div>
    );
  }
}
